import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { getSupabaseClient } from '../../lib/supabase';
import { 
  TrendingUp, 
  Search, 
  Plus, 
  IndianRupee, 
  Calendar, 
  Printer, 
  RefreshCw, 
  CheckCircle2, 
  AlertCircle,
  FileText,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import { ServiceVoucherPrintModal } from './ServiceVoucherPrintModal';

export function ServicesOtherIncomeView({ branchId }: { branchId?: string | null } = {}) {
  const { currentTenantId, activeBranchId } = useAuth();
  const supabase = getSupabaseClient();
  const effectiveBranchId = branchId || activeBranchId;

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<any[]>([]);
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Form states
  const [categoryId, setCategoryId] = useState('');
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [addingCat, setAddingCat] = useState(false);

  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'upi' | 'card' | 'bank' | 'online' | 'cheque'>('cash');
  const [incomeDate, setIncomeDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [receivedFrom, setReceivedFrom] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Voucher print state
  const [printVoucherId, setPrintVoucherId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!supabase || !currentTenantId) return;
    setLoading(true);

    try {
      // 1. Ensure default categories
      try {
        await supabase.rpc('ensure_service_default_categories', {
          p_tenant_id: currentTenantId
        });
      } catch (e) {
        console.warn('ensure_service_default_categories error:', e);
      }

      // 2. Fetch other income categories
      const { data: catData, error: catErr } = await supabase
        .from('other_income_categories')
        .select('*')
        .eq('tenant_id', currentTenantId)
        .eq('is_active', true)
        .order('category_name', { ascending: true });

      if (!catErr && catData) {
        setCategories(catData);
      }

      // 3. Fetch income vouchers
      const { data: vData, error: vErr } = await supabase.rpc('list_service_vouchers', {
        p_tenant_id: currentTenantId,
        p_kind: 'income',
        p_branch_id: effectiveBranchId || null,
        p_limit: 100
      });

      if (vErr) {
        console.error('list_service_vouchers income error:', vErr);
        toast.error('Failed to load other income: ' + vErr.message);
      } else if (Array.isArray(vData)) {
        setVouchers(vData);
      }
    } catch (err: any) {
      console.error('loadData error:', err);
      toast.error(err.message || 'Error loading other income');
    } finally {
      setLoading(false);
    }
  }, [supabase, currentTenantId, effectiveBranchId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !currentTenantId || !newCatName.trim()) return;

    setAddingCat(true);
    try {
      const { data, error } = await supabase
        .from('other_income_categories')
        .insert({
          tenant_id: currentTenantId,
          category_name: newCatName.trim(),
          is_active: true
        })
        .select()
        .single();

      if (error) {
        toast.error(error.message);
      } else if (data) {
        toast.success(`Category "${data.category_name}" added`);
        setCategories(prev => [...prev, data]);
        setCategoryId(data.id);
        setNewCatName('');
        setShowNewCat(false);
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to add category');
    } finally {
      setAddingCat(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !currentTenantId) return;

    if (!categoryId) {
      toast.error('Please select an income category');
      return;
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.rpc('service_record_income', {
        p_tenant_id: currentTenantId,
        p_branch_id: effectiveBranchId || null,
        p_category_id: categoryId,
        p_amount: numAmount,
        p_payment_method: paymentMethod,
        p_income_date: incomeDate || new Date().toISOString().slice(0, 10),
        p_description: description.trim() || null,
        p_received_from: receivedFrom.trim() || null
      });

      if (error) {
        console.error('service_record_income error:', error);
        toast.error(error.message);
      } else {
        toast.success(`Other Income of ₹${numAmount.toFixed(2)} recorded!`);
        setAmount('');
        setDescription('');
        setReceivedFrom('');
        loadData();
        if (data) {
          setPrintVoucherId(data);
        }
      }
    } catch (err: any) {
      console.error('Submit income error:', err);
      toast.error(err.message || 'Failed to record income');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredVouchers = vouchers.filter(v => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (v.category || '').toLowerCase().includes(q) ||
      (v.party || '').toLowerCase().includes(q) ||
      (v.method || '').toLowerCase().includes(q) ||
      (v.note || '').toLowerCase().includes(q)
    );
  });

  const totalIncome = vouchers.reduce((acc, v) => acc + (Number(v.amount) || 0), 0);

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <TrendingUp className="text-teal-600" size={24} />
            <span>Other Income (Miscellaneous Inflows)</span>
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Record scrap sales, tool rental deposits forfeiture, referral commissions, and non-billing revenue
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={loadData}
            disabled={loading}
            className="text-xs h-9 gap-1.5"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Record Income Form */}
        <div className="lg:col-span-5 space-y-4">
          <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm rounded-2xl">
            <CardContent className="p-5 space-y-4">
              <h3 className="font-bold text-slate-900 dark:text-white text-sm pb-2 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
                <Plus size={16} className="text-teal-600" />
                <span>Record Other Income</span>
              </h3>

              <form onSubmit={handleSubmit} className="space-y-3.5 text-xs">
                {/* Category Dropdown */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="font-semibold text-slate-700 dark:text-slate-300">
                      Income Category <span className="text-rose-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowNewCat(!showNewCat)}
                      className="text-[11px] font-semibold text-primary hover:underline flex items-center gap-0.5"
                    >
                      <Plus size={12} />
                      <span>{showNewCat ? 'Cancel' : 'New Category'}</span>
                    </button>
                  </div>

                  {showNewCat ? (
                    <div className="flex gap-2 mb-2 p-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                      <Input
                        value={newCatName}
                        onChange={e => setNewCatName(e.target.value)}
                        placeholder="e.g. Scrap Sold, Tool Rental"
                        className="h-8 text-xs bg-white dark:bg-slate-900"
                      />
                      <Button
                        type="button"
                        size="sm"
                        disabled={addingCat || !newCatName.trim()}
                        onClick={handleAddCategory}
                        className="h-8 text-xs px-3 font-semibold"
                      >
                        {addingCat ? 'Adding...' : 'Add'}
                      </Button>
                    </div>
                  ) : null}

                  <select
                    value={categoryId}
                    onChange={e => setCategoryId(e.target.value)}
                    className="w-full h-10 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white outline-none"
                    required
                  >
                    <option value="">-- Choose Category --</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.category_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Amount & Date */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                      Amount (₹) <span className="text-rose-500">*</span>
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={amount}
                      onChange={e => setAmount(e.target.value)}
                      placeholder="0.00"
                      required
                      className="h-10 text-xs font-mono font-bold"
                    />
                  </div>

                  <div>
                    <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                      Date
                    </label>
                    <Input
                      type="date"
                      value={incomeDate}
                      onChange={e => setIncomeDate(e.target.value)}
                      className="h-10 text-xs font-mono"
                    />
                  </div>
                </div>

                {/* Payment Method */}
                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Payment Method
                  </label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {(['cash', 'upi', 'card', 'bank', 'online', 'cheque'] as const).map(m => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setPaymentMethod(m)}
                        className={`py-2 px-1 rounded-xl text-[11px] font-bold uppercase transition-all border ${
                          paymentMethod === m
                            ? 'bg-teal-600 text-white border-teal-600 shadow-xs'
                            : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Received From */}
                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Received From / Party Name (optional)
                  </label>
                  <Input
                    value={receivedFrom}
                    onChange={e => setReceivedFrom(e.target.value)}
                    placeholder="e.g. Scrap Buyer, Affiliate Partner"
                    className="h-9 text-xs"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Description (optional)
                  </label>
                  <Input
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="e.g. Copper wire scrap sold"
                    className="h-9 text-xs"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting || !categoryId || !amount}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs h-10 rounded-xl shadow-md gap-1.5 mt-2"
                >
                  {submitting ? (
                    <RefreshCw size={14} className="animate-spin" />
                  ) : (
                    <CheckCircle2 size={15} />
                  )}
                  <span>Record Income & Print Slip</span>
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Incomes History */}
        <div className="lg:col-span-7 space-y-4">
          {/* Stats Bar */}
          <div className="p-3.5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs flex items-center justify-between">
            <div>
              <span className="text-[11px] uppercase font-bold text-slate-400 block">Total Inflow</span>
              <span className="text-xl font-bold font-mono text-teal-600 dark:text-teal-400">
                ₹{totalIncome.toFixed(2)}
              </span>
            </div>
            <span className="text-xs text-slate-500">
              {vouchers.length} Entries
            </span>
          </div>

          {/* Search bar */}
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search category, received from, method..."
              className="pl-9 h-9 text-xs bg-white dark:bg-slate-900"
            />
          </div>

          {/* Table */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs">
            {loading ? (
              <div className="p-12 text-center text-slate-400 text-xs flex flex-col items-center justify-center gap-2">
                <RefreshCw size={20} className="animate-spin text-primary" />
                <span>Loading income records...</span>
              </div>
            ) : filteredVouchers.length === 0 ? (
              <div className="p-10 text-center text-slate-400 space-y-2">
                <FileText size={28} className="mx-auto text-slate-300 dark:text-slate-600" />
                <p className="text-xs font-semibold">No other income records found</p>
                <p className="text-[11px] text-slate-400">Record miscellaneous income from the left form.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] uppercase font-bold text-slate-500 border-b border-slate-100 dark:border-slate-800">
                    <tr>
                      <th className="p-3">Date</th>
                      <th className="p-3">Category</th>
                      <th className="p-3">Received From</th>
                      <th className="p-3">Mode</th>
                      <th className="p-3 text-right">Amount</th>
                      <th className="p-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {filteredVouchers.map(v => (
                      <tr key={v.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40 transition-colors">
                        <td className="p-3 text-slate-500 whitespace-nowrap">
                          {v.date ? new Date(v.date).toLocaleDateString('en-IN') : '-'}
                        </td>
                        <td className="p-3 font-semibold text-slate-900 dark:text-white">
                          {v.category || 'Income'}
                        </td>
                        <td className="p-3 text-slate-600 dark:text-slate-300">
                          {v.party || '-'}
                        </td>
                        <td className="p-3">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                            {v.method || 'cash'}
                          </span>
                        </td>
                        <td className="p-3 text-right font-mono font-bold text-teal-600 dark:text-teal-400">
                          ₹{Number(v.amount || 0).toFixed(2)}
                        </td>
                        <td className="p-3 text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPrintVoucherId(v.id)}
                            className="h-7 px-2.5 text-[11px] gap-1 font-semibold text-slate-700 dark:text-slate-300"
                          >
                            <Printer size={12} />
                            <span>Print</span>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Voucher Print Modal */}
      {printVoucherId && (
        <ServiceVoucherPrintModal
          kind="income"
          voucherId={printVoucherId}
          isOpen={!!printVoucherId}
          onClose={() => setPrintVoucherId(null)}
        />
      )}
    </div>
  );
}

export default ServicesOtherIncomeView;
